import { useEffect, useId } from 'react'
import { CalendarClock } from 'lucide-react'
import type { TerugkerendVelden } from '../../../gedeeld/types'
import { DAGEN, FREQUENTIES, schemaZin, vandaagInvoer } from '../../lib/opmaak'
import { useMag } from '../../lib/staat'

/* ===========================================================================
 *  De velden van een terugkerende taak
 *
 *  Eén formulier voor twee plekken: de kaart in het gesprek (als Cowork
 *  voorstelt een taak in te plannen) en de editor op /taken. Zo is een
 *  taak die je uit een voorstel uitvoert precies dezelfde als een die je
 *  zelf maakt, en kun je in beide alles aanpassen wat de AI invulde.
 *
 *  Onder de velden staat het schema in een gewone zin ("Elke werkdag om
 *  08:00"), want "wekelijks, 1, 08:00" lees je verkeerd en een zin niet.
 * =========================================================================== */

export type Fouten = Partial<Record<keyof TerugkerendVelden, string>>

export default function TerugkerendFormulier({ v, onV, fouten, compact, uit }: {
  v: TerugkerendVelden
  onV: (v: TerugkerendVelden) => void
  fouten?: Fouten
  /** In een kaart in het gesprek: iets dichter op elkaar. */
  compact?: boolean
  /** Alleen lezen, bijvoorbeeld nadat het voorstel is uitgevoerd. */
  uit?: boolean
}) {
  const id = useId()
  const magMailen = useMag('cowork.mailen')
  const zet = <K extends keyof TerugkerendVelden>(k: K, w: TerugkerendVelden[K]) => onV({ ...v, [k]: w })
  const f = fouten ?? {}

  /*
   * Mailen staat voor deze persoon uit (recht cowork.mailen), maar het vinkje
   * stond aan -- uit een sjabloon, of omdat de AI het zo voorstelde. Het
   * vinkje toonde dan uit en was niet aan te raken, terwijl er "mailen: ja"
   * werd verstuurd; de server weigerde en zei "zet het vinkje bij mailen
   * uit". Dat kon je niet. Nu staat wat je ziet ook in wat er weggaat.
   */
  useEffect(() => {
    if (!magMailen && v.mailen && !uit) onV({ ...v, mailen: false })
  }, [magMailen, v.mailen, uit]) // eslint-disable-line react-hooks/exhaustive-deps
  const fout = (k: keyof TerugkerendVelden) => f[k]
    ? <span className="veld-fout" id={`${id}-${k}-fout`} role="alert">{f[k]}</span>
    : null
  const beschreven = (k: keyof TerugkerendVelden) => (f[k] ? `${id}-${k}-fout` : undefined)

  return (
    <fieldset className={`formulier tk-form ${compact ? 'compact' : ''}`} disabled={uit}>
      <label className="veld breed">
        <span className="veld-label">Naam</span>
        <input className="invoer" value={v.naam} maxLength={120} onChange={(e) => zet('naam', e.target.value)}
          placeholder="Bijvoorbeeld: openstaande facturen op maandag" aria-invalid={!!f.naam} aria-describedby={beschreven('naam')} />
        {fout('naam')}
      </label>

      <label className="veld breed">
        <span className="veld-label">Opdracht voor Cowork</span>
        <textarea className="invoer" rows={compact ? 3 : 4} value={v.opdracht} onChange={(e) => zet('opdracht', e.target.value)}
          placeholder="Schrijf het op zoals je het Cowork zou vragen, bijvoorbeeld: welke inkoopfacturen staan er nog open, per bv, en welke zijn te laat?"
          aria-invalid={!!f.opdracht} aria-describedby={beschreven('opdracht')} />
        {fout('opdracht')}
      </label>

      <div className="veld breed">
        <span className="veld-label" id={`${id}-freq`}>Hoe vaak</span>
        <div className="segment" role="radiogroup" aria-labelledby={`${id}-freq`}>
          {FREQUENTIES.map((x) => (
            <button key={x.key} type="button" role="radio" aria-checked={v.frequentie === x.key}
              className={`segment-knop ${v.frequentie === x.key ? 'aan' : ''}`}
              onClick={() => onV({
                ...v,
                frequentie: x.key,
                /* Wat bij de nieuwe keuze hoort een zinnige beginwaarde geven,
                   zodat de zin eronder meteen klopt. */
                dagen: x.key === 'wekelijks' && !v.dagen.length ? [1] : v.dagen,
                dagVanMaand: x.key === 'maandelijks' && !v.dagVanMaand ? 1 : v.dagVanMaand,
                datum: x.key === 'eenmalig' && !v.datum ? vandaagInvoer(1) : v.datum,
              })}>
              {x.label}
            </button>
          ))}
        </div>
      </div>

      {v.frequentie === 'wekelijks' && (
        <div className="veld breed">
          <span className="veld-label" id={`${id}-dagen`}>Op welke dagen</span>
          <div className="dagen" role="group" aria-labelledby={`${id}-dagen`} aria-describedby={beschreven('dagen')}>
            {DAGEN.map((d) => {
              const aan = v.dagen.includes(d.nr)
              return (
                <button key={d.nr} type="button" className={`dag-knop ${aan ? 'aan' : ''}`} aria-pressed={aan}
                  aria-label={d.lang} title={d.lang}
                  onClick={() => zet('dagen', aan ? v.dagen.filter((x) => x !== d.nr) : [...v.dagen, d.nr].sort((a, b) => a - b))}>
                  {d.kort}
                </button>
              )
            })}
          </div>
          {fout('dagen')}
        </div>
      )}

      {v.frequentie === 'maandelijks' && (
        <label className="veld">
          <span className="veld-label">Dag van de maand</span>
          <select className="invoer" value={v.dagVanMaand ?? ''} onChange={(e) => zet('dagVanMaand', e.target.value ? Number(e.target.value) : null)}
            aria-invalid={!!f.dagVanMaand} aria-describedby={beschreven('dagVanMaand')}>
            {Array.from({ length: 28 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>De {n}e</option>)}
          </select>
          <span className="veld-hulp">Tot en met de 28e, zodat hij ook in februari draait.</span>
          {fout('dagVanMaand')}
        </label>
      )}

      {v.frequentie === 'eenmalig' && (
        <label className="veld">
          <span className="veld-label">Datum</span>
          <input type="date" className="invoer" value={v.datum ?? ''} min={vandaagInvoer()}
            onChange={(e) => zet('datum', e.target.value || null)} aria-invalid={!!f.datum} aria-describedby={beschreven('datum')} />
          {fout('datum')}
        </label>
      )}

      <label className="veld">
        <span className="veld-label">Tijd</span>
        <input type="time" className="invoer" value={v.tijd} step={300} onChange={(e) => zet('tijd', e.target.value)}
          aria-invalid={!!f.tijd} aria-describedby={beschreven('tijd')} />
        <span className="veld-hulp">Nederlandse tijd.</span>
        {fout('tijd')}
      </label>

      <div className="veld breed">
        <span className="veld-label">Als hij klaar is</span>
        <div className="vinkjes">
          <label className="vinkje">
            <input type="checkbox" checked={v.melden} onChange={(e) => zet('melden', e.target.checked)} />
            Een melding in de bel van het dashboard
          </label>
          <label className="vinkje" title={magMailen ? undefined : 'Mailen staat voor jou uit; het management zet dat aan bij je rechten.'}>
            <input type="checkbox" checked={v.mailen && magMailen} disabled={!magMailen} onChange={(e) => zet('mailen', e.target.checked)} />
            Een mail naar je werkadres{!magMailen && <span className="stil"> (staat voor jou uit)</span>}
          </label>
        </div>
      </div>

      <p className="schema-zin breed" aria-live="polite">
        <CalendarClock size={15} aria-hidden />
        <span>{schemaZin(v)}</span>
      </p>
    </fieldset>
  )
}
